import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {
    BackButton, BoxTypeSelectionResult,
    classNames,
    layout,
    PendingView,
    PushButton,
    useAppEventSupport,
    useAsync,
    useBarcodeListener,
    useBoxes,
    usePlatform,
    useStorageItemValue,
    View,
    ViewContent,
    ViewDescription,
    ViewHeader,
    ViewHeaderItem,
    ViewTitle,
} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import {useAuthenticatedCarrier, useCarriers, useSetAuthenticatedCarrier} from "./CarrierProvider";
import {stylesheet} from "typestyle";
import IconDelivery from "./icon-usecase_delivery.svg";
import IconPickup from "./icon-usecase_pickup.svg";
import {BackendApiCredentials, CubeCarrier, CubeCarrierState, getDeliveryOccupancyContent} from './types';
import {AccessCodeAlphabet, Barcode, Box, LogLevel, Occupancy} from '@variocube/app-api';
import {logger} from './logger';
import {CarrierRetrieval} from './carrier-retrieval';
import {CarrierStore} from './carrier-store/carrier-store';
import {parseBarcode} from './barcode';
import {InvalidCode, InvalidCodeDialog, InvalidCodeType} from './InvalidCodeDialog';
import {CarrierCodeFormView} from './CarrierCodeFormView';
import {SizeSelectionView} from './SizeSelectionView';
import {BoxClose} from './BoxClose';
import {useSetMessage} from './DialogMessageProvider';
import {CarrierStoreEventHandler} from './carrier-store/carrier-store-event-handlers';
import {useLocalization} from "../i18n";

const log = logger.sub("CarrierAuthenticatedView");

interface CarrierAuthenticatedProps {
    backendApiCredentials?: BackendApiCredentials;
}

export function CarrierAuthenticated({ backendApiCredentials }: CarrierAuthenticatedProps) {
    const authenticatedCarrier: string = useAuthenticatedCarrier()!;
    const platform = usePlatform();
    const {t} = useLocalization();
    const [carrierRetrieval, setCarrierRetrieval] = useState<Occupancy[]>();
    const [carrierDeliverySelected, setCarrierDeliverySelected] = useState<boolean>();
    const setAuthenticatedCarrier = useSetAuthenticatedCarrier();
    const carriers = useCarriers();
    const cubeCarrier = useMemo<CubeCarrier|undefined>(() => {
        if(carriers && authenticatedCarrier) {
            return carriers[authenticatedCarrier];
        }
    }, [carriers, authenticatedCarrier]);

    const lastMileEnabled = useMemo<boolean>(() => {
        if(carriers && authenticatedCarrier) {
            const carrier = carriers[authenticatedCarrier];
            if(carrier && carrier.state) {
                switch (carrier.state) {
                    case CubeCarrierState.Disabled:
                    case CubeCarrierState.FirstMileOnly:
                        return false;
                }
            }
        }
        return true;
    }, [carriers, authenticatedCarrier]);

    const carrierDeliveryOptionAvailable = useMemo<boolean | undefined>(() => {
        return !!cubeCarrier?.lastMile?.carrierInterfaceShowsDeliveryOption && lastMileEnabled;
    }, [cubeCarrier]);

    useAsync(async () => {
        if (!carrierDeliveryOptionAvailable) {
            await handleCarrierRetrievalSelected();
        }
    }, []);

    const handleLogout = useCallback(() => setAuthenticatedCarrier(undefined), [setAuthenticatedCarrier]);
    const handleCarrierDeliveryDone = useCallback(() => setCarrierDeliverySelected(false), [setCarrierDeliverySelected]);
    const handleCarrierRetrievalDone = useCallback(() => {
        if (!carrierDeliveryOptionAvailable) {
            handleLogout();
        } else {
            setCarrierRetrieval(undefined);
        }
    }, [setCarrierRetrieval]);


    const handleCarrierDeliverySelected = useCallback(() => {
        setCarrierDeliverySelected(true);
    }, [carrierDeliverySelected]);
    const handleCarrierRetrievalSelected = useCallback(async () => {
        const occupancies = await platform.getOccupancies();
        const retrievable = occupancies.filter(isCarrierRetrievable(authenticatedCarrier));
        log.info("Carrier retrievable", retrievable);
        setCarrierRetrieval(retrievable);
    }, [setCarrierRetrieval, platform]);

    if (carrierRetrieval) {
        return <CarrierRetrievalView carrierRetrieval={carrierRetrieval} handleLogout={handleLogout} onCarrierRetrievalDone={handleCarrierRetrievalDone} autoCarrierRetrieval={cubeCarrier?.autoCarrierRetrieval} />;
    } else if (carrierDeliverySelected) {
        return <CarrierDeliveryView cubeCarrier={cubeCarrier} backendApiCredentials={backendApiCredentials} onCarrierDeliveryDone={handleCarrierDeliveryDone} handleLogout={handleLogout} authenticatedCarrier={authenticatedCarrier} />;
    } else if (carrierDeliveryOptionAvailable) {
        return <ActionSelectionView handleCarrierRetrievalChosen={handleCarrierRetrievalSelected}
                                    handleCarrierDeliveryChosen={handleCarrierDeliverySelected}
                                    handleLogout={handleLogout}/>;
    }
    return <PendingView message={t("Loading")} />;
}

interface CarrierDeliveryViewProps {
    authenticatedCarrier: string;
    backendApiCredentials?: BackendApiCredentials;
    handleLogout: () => void;
    onCarrierDeliveryDone: () => void;
    cubeCarrier?: CubeCarrier;
}

function CarrierDeliveryView({onCarrierDeliveryDone, handleLogout, backendApiCredentials, authenticatedCarrier, cubeCarrier}: CarrierDeliveryViewProps) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const boxes = useBoxes();
    const expiredParcelIds = useStorageItemValue<string[] | undefined>("expiredDeliveryIds");
    const [carrierStore, setCarrierStore] = useState<Occupancy>();
    const [invalidCode, setInvalidCode] = useState<InvalidCode | undefined>();
    const [showSizeSelection, setShowSizeSelection] = useState<boolean>();
    const [carrierStoreParcelNo, setCarrierStoreParcelNo] = useState<string>();
    const [closeBox, setCloseBox] = useState<Box>();
    const setMessage = useSetMessage();
    const [loading, setLoading] = useState<boolean>();
    const carrierStoreEventHandler = useMemo(() => new CarrierStoreEventHandler(), []);

    const onCarrierStore = useCallback((occupancy: Occupancy) => {
        setCarrierStore(occupancy);
    }, [setCarrierStore]);

    const onCarrierStoreConfirm = useCallback(async (occupancy: Occupancy) => {
        try {
            setLoading(true);
            await carrierStoreEventHandler.parcelStoreConfirmed({
                carrierId: authenticatedCarrier!,
                cubeId: platform.hostname,
                backendApiCredentials: backendApiCredentials!,
                parcelId: occupancy.content.deliveryId,
                isAdhocDelivery: occupancy.content.isAdhocDelivery,
                strings: t,
                setMessage
            });
            setMessage({
                title: t("ParcelStoreSuccessTitle"),
                message: t("ParcelStoreSuccessDescription"),
            });
            await platform.updateOccupancy(occupancy.uuid, {
                storedAt: DateTime.utc().toISO(),
            }, true);
            await platform.log(LogLevel.Audit, "Carrier stored", {occupancy});
        } catch (err) {
            console.error(err);
            setMessage({
                title: t("ParcelNotAcceptedTitle"),
                message: t("ParcelNotAcceptedDescription", { barCode: occupancy.content.deliveryId }),
            });
            await platform.endOccupancy(occupancy.uuid);
            await platform.openBoxLock(occupancy.boxNumber);
            const box = boxes.find(box => box.number == occupancy.boxNumber);
            setCloseBox(box);
        } finally {
            setLoading(false);
        }
    }, [cubeCarrier, boxes]);

    const onCarrierStoreDone = useCallback(async () => {
        setCarrierStore(undefined);
        setCarrierStoreParcelNo(undefined);
    }, []);

    const clearOpenBox = useCallback(() => {
        setCloseBox(undefined);
    }, []);

    const handleCarrierFormSubmit = useCallback(async (code: string) => {
        // workaround to allow entering full barcode with keyboard
        await handleBarcode({code: btoa(code)});
    }, []);

    const handleCodeVerify = useCallback(async (code: string) => {
        setInvalidCode(undefined);
        log.info("Checking code", code);

        if (code) {
            const occupancy = (await platform.getOccupancies(code)).pop();
            if (occupancy) {
                log.info("Found occupancy", occupancy);
                const delivery = getDeliveryOccupancyContent(occupancy);
                if (delivery) {
                    switch (code) {
                        case delivery.deliveryId:
                        case delivery.carrierDeliveryCode:
                            return onCarrierStore(occupancy);
                    }
                }
            }
        }
        throw new Error("Invalid code");
    }, [platform, onCarrierStore]);

    const handleBarcode = useCallback(async (barcode: Barcode) => {
        const barCode = atob(barcode.code);

        // check if it is carrier delivery code first
        try {
            await handleCodeVerify(barCode);
        } catch (e) {
            log.info("barcode was not carrier delivery code - continue");
        }

        const result = parseBarcode(barCode);
        if (result) {
            // make sure, type is inbound in this view
            result.type = "inbound";
            const {carrier, parcelNo} = result;
            console.log(`Checking parcelNo: ${parcelNo} of carrier: ${carrier} against list of expired parcel numbers`);
            if (expiredParcelIds && expiredParcelIds.indexOf(parcelNo) != -1) {
                setInvalidCode({
                    type: InvalidCodeType.Expired,
                    code: parcelNo
                });
                return;
            }
            try {
                log.info("Found parcelNo in barcode. Trying parcelNo for access code", result);
                return await handleCodeVerify(parcelNo);
            } catch (error) {
                log.info("Parcel number was not valid for carrier store - check against carrier API");
                setCarrierStoreParcelNo(parcelNo);
                setShowSizeSelection(true);
            }
        } else {
            setInvalidCode({
                type: InvalidCodeType.Invalid,
                code: barCode
            });
        }
    }, [handleCodeVerify, expiredParcelIds, authenticatedCarrier]);
    useBarcodeListener(handleBarcode);

    const sizeSelectionAborted = useCallback(() => {
        setShowSizeSelection(false);
        setCarrierStoreParcelNo(undefined);
    }, [setCarrierStoreParcelNo, setShowSizeSelection]);

    const sizeSelectionFinished = useCallback(async (typeSelectionResult: BoxTypeSelectionResult) => {
        setShowSizeSelection(false);
        const occupancy = await platform.occupy({
            ...typeSelectionResult,
            accessCodeShape: {
                alphabet: AccessCodeAlphabet.OneToNine,
                length: 6,
            },
            content: {
                deliveryId: carrierStoreParcelNo,
                carrierId: authenticatedCarrier,
                isAdhocDelivery: true,
            },
        });
        await platform.confirmOccupancy(occupancy.uuid);
        setCarrierStore(occupancy);
    }, [carrierStoreParcelNo, authenticatedCarrier]);

    const renderView = useMemo(() => {
        if (loading) return <PendingView message={t("ProcessingParcelStore")} />;
        else if (closeBox) {
            return <BoxClose box={closeBox} onDone={clearOpenBox}/>;
        }
        else if (showSizeSelection) {
            return (
                <SizeSelectionView
                    onBack={sizeSelectionAborted}
                    onSelect={sizeSelectionFinished}
                />
            );
        }
        else if (carrierStore) {
            return (
                <CarrierStore onDone={onCarrierStoreDone} reservation={carrierStore} onConfirm={onCarrierStoreConfirm}
                              preventOccupancyStoredAtUpdate/>
            );
        }
        return (
            <CarrierCodeFormView
                handleCodeVerify={handleCarrierFormSubmit}
                setInvalidCode={setInvalidCode}
                carrier={authenticatedCarrier}
                invalidCode={invalidCode}
                hideHeader
            />
        );
    }, [loading, showSizeSelection, carrierStore, authenticatedCarrier, invalidCode, closeBox]);

    return (
        <View>
            <CarrierAuthenticatedViewHeader handleBack={onCarrierDeliveryDone} authenticatedCarrier={authenticatedCarrier} handleLogout={handleLogout} descriptionText={t("CarrierParcelDelivery") as string} />
            <ViewContent
                className={classNames(layout.fill)}>
                {renderView}
            </ViewContent>
            <InvalidCodeDialog invalidCode={invalidCode} onClose={() => setInvalidCode(undefined)}/>
        </View>
    );
}

interface CarrierRetrievalViewProps {
    carrierRetrieval: Occupancy[];
    handleLogout: () => void;
    onCarrierRetrievalDone: () => void;
    autoCarrierRetrieval?: boolean;
}

function CarrierRetrievalView({handleLogout, onCarrierRetrievalDone, carrierRetrieval, autoCarrierRetrieval}: CarrierRetrievalViewProps) {
    const authenticatedCarrier = useAuthenticatedCarrier();
    const {t} = useLocalization();
    return (
        <View>
            <CarrierAuthenticatedViewHeader handleBack={onCarrierRetrievalDone} authenticatedCarrier={authenticatedCarrier} handleLogout={handleLogout} descriptionText={t("ChooseRetrieveParcels") as string} />
            <ViewContent
                className={classNames(layout.fill)}>
                <CarrierRetrieval occupancies={carrierRetrieval} onDone={onCarrierRetrievalDone} autoCarrierRetrieval={autoCarrierRetrieval} />
            </ViewContent>
        </View>
    );
}

interface ActionSelectionViewProps {
    handleCarrierRetrievalChosen: () => void;
    handleCarrierDeliveryChosen: () => void;
    handleLogout: () => void;
}

function ActionSelectionView({handleCarrierRetrievalChosen, handleCarrierDeliveryChosen, handleLogout}: ActionSelectionViewProps) {
    //const strings = useStrings(resources);
    const {t} = useLocalization();
    const authenticatedCarrier = useAuthenticatedCarrier();
    return (
        <View>
            <CarrierAuthenticatedViewHeader handleBack={handleLogout} authenticatedCarrier={authenticatedCarrier} handleLogout={handleLogout} descriptionText={t("CarrierAuthenticationDescription") as string} />
            <ViewContent
                className={classNames(layout.fill, layout.horizontal, layout.center, layout.alignCenter)}>
                <ActionViewItem
                    itemClass={styles.firstItem}
                    iconClass={styles.deliveryIcon}
                    text={t("Deliver") as string}
                    onClick={handleCarrierDeliveryChosen}
                />
                <ActionViewItem
                    iconClass={styles.pickUpIcon}
                    text={t("Retrieve") as string}
                    onClick={handleCarrierRetrievalChosen}
                />
            </ViewContent>
        </View>
    );
}

type ActionViewItemProps = {
    itemClass?: string;
    iconClass: string;
    text: string;
    onClick: () => void;
}

function ActionViewItem ({itemClass, iconClass, text, onClick}: ActionViewItemProps) {
    return (
        <div className={classNames(styles.item, itemClass)} onClick={onClick}>
            <div className={classNames(styles.itemIcon, iconClass)}/>
            <div className={styles.itemText}>{text}</div>
        </div>
    );
}

type CarrierAuthenticatedViewHeaderProps = {
    handleLogout: () => void;
    authenticatedCarrier?: string;
    descriptionText: string;
    handleBack: () => void;
}


function CarrierAuthenticatedViewHeader({handleLogout, handleBack, authenticatedCarrier, descriptionText}: CarrierAuthenticatedViewHeaderProps) {
    const {t} = useLocalization();
    const appEventSupport = useAppEventSupport();

    const handleLogoutIfNotDisabled = useCallback(() => {
        // prevent logout when preventAutoDeactivate, driver should not be able to abort process, when box is open
        if (appEventSupport.canAutoDeactivate()) {
            handleLogout();
        }
    }, [appEventSupport, handleLogout]);

    const handleBackIfNotDisabled = useCallback(() => {
        // prevent logout when preventAutoDeactivate, driver should not be able to abort process, when box is open
        if (appEventSupport.canAutoDeactivate()) {
            handleBack();
        }
    }, [appEventSupport, handleBack]);

    return (
        <ViewHeader className={styles.viewHeader}>
            <ViewHeaderItem>
                <BackButton onClick={handleBackIfNotDisabled} label={t("Back")}/>
            </ViewHeaderItem>
            <ViewHeaderItem fill>
                <ViewTitle>{t("CarrierAuthenticatedTitle", {carrier: authenticatedCarrier?.toUpperCase()})}</ViewTitle>
                <ViewDescription>{descriptionText}</ViewDescription>
            </ViewHeaderItem>
            <ViewHeaderItem className={styles.logoutWrapper}>
                <PushButton onClick={handleLogoutIfNotDisabled}>{t("Logout")}</PushButton>
            </ViewHeaderItem>
        </ViewHeader>
    );
}

export const styles = stylesheet({
    viewHeader: {
        boxShadow: '0 0.1em 0.3em -0.1em rgba(0, 0, 0, 0.5)',
        zIndex: 1,
    },
    selectionViewContent: {
        background: '#f6f6f6',
    },
    item: {
        background: "var(--color-primary)",
        color: "#fff",
        boxShadow: "0 0.25em 0.75em -0.25em #000",
        height: "27.5vw",
        width: "27.5vw",
        padding: "2em 3em",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
    },
    itemIcon: {
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        height: "5em",
        width: "7em",
        margin: "1em auto",
    },
    itemText: {
        fontWeight: 600,
        fontSize: "2em",
    },
    deliveryIcon: {
        backgroundImage: `url(${IconDelivery})`,
    },
    pickUpIcon: {
        backgroundImage: `url(${IconPickup})`,
    },
    firstItem: {
        marginRight: "32px",
    },
    logoutWrapper: {
        paddingRight: "1em"
    }
});

function isCarrierRetrievable(carrierId: string) {
    return (occupancy: Occupancy) => {
        const delivery = getDeliveryOccupancyContent(occupancy);
        return delivery && delivery.carrierId == carrierId && (delivery.barCode || delivery.carrierRetrievalCode == occupancy.accessCode);
    };
}